import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, Loader, Upload, X, Shield } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import Navbar from '../components/Navbar';
import AutocompleteSelect from '../components/AutocompleteSelect';
import OpeningHoursEditor from '../components/OpeningHoursEditor';
import PinLocationPicker from '../components/PinLocationPicker';
import SignatureServices from '../components/SignatureServices';
import TermsConsent from '../components/TermsConsent';
import { getMe, getCounties, getInsurances, getSpecialties } from '../services/api';
import { toast } from 'sonner';
import api from '../services/api';

const FACILITY_TYPES = ["Clinic", "Dental Clinic", "Optical Clinic", "Hospital", "Multi-specialty Hospital"];
const HOSPITAL_LEVELS = [
  "Level 1 - Community Health Services", "Level 2 - Dispensaries & Clinics",
  "Level 3 - Health Centres", "Level 4 - Sub-county Hospitals",
  "Level 5 - County Referral Hospitals", "Level 6 - National Referral Hospitals"
];
const IMAGING_MODALITIES = [
  "X-Ray", "Ultrasound", "CT Scan", "MRI", "Mammography",
  "Fluoroscopy", "PET Scan", "DEXA Scan", "Echocardiography", "Doppler Ultrasound"
];
const DAYS_OF_WEEK = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

export default function FacilityRegistration() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [kmpdcVerified, setKmpdcVerified] = useState(null);
  const [uploadingImages, setUploadingImages] = useState(false);

  const [counties, setCounties] = useState([]);
  const [insurances, setInsurances] = useState([]);
  const [specialties, setSpecialties] = useState([]);
  const [facilityServices, setFacilityServices] = useState({});

  const [formData, setFormData] = useState({
    facility_name: '',
    location: '',
    region: '',
    pin_location: null,
    contact_person_name: '',
    contact_person_designation: '',
    contact_email: '',
    contact_phone: '',
    bio: '',
    facility_type: '',
    hospital_level: '',
    kmpdc_registration: '',
    is_24_7: false,
    opening_hours: { weekdays: '08:00 - 17:00', saturday: '09:00 - 13:00', sunday: 'Closed', public_holidays: 'Closed' },
    specialties_available: {},
    imaging_modalities: [],
    services_available: [],
    insurances_accepted: [],
    facility_images: [],
    signature_services: [],
    terms_accepted: false
  });

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await getMe();
        setUser(response.data);
        setFormData(prev => ({ ...prev, contact_person_name: response.data.name, contact_email: response.data.email }));
      } catch { setUser(null); }
    };
    checkAuth();

    const loadOptions = async () => {
      try {
        const [countiesRes, insurancesRes, specialtiesRes, servicesRes] = await Promise.all([
          getCounties(), getInsurances(), getSpecialties(), api.get('/metadata/facility-services')
        ]);
        setCounties(countiesRes.data || []);
        setInsurances(insurancesRes.data || []);
        setSpecialties(specialtiesRes.data || []);
        setFacilityServices(servicesRes.data || {});
      } catch (error) {
        console.error('Error loading options:', error);
      }
    };
    loadOptions();
  }, []);

  const handleVerifyKMPDC = async () => {
    if (!formData.kmpdc_registration) return;
    setVerifying(true);
    try {
      const response = await api.post('/kmpdc/verify', { license_number: formData.kmpdc_registration });
      setKmpdcVerified(response.data.verified);
      toast[response.data.verified ? 'success' : 'error'](response.data.message || (response.data.verified ? 'KMPDC verified!' : 'Could not verify'));
    } catch { toast.error('Verification failed'); }
    setVerifying(false);
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploadingImages(true);
    const urls = [];
    for (const file of files.slice(0, 5)) {
      const fd = new FormData();
      fd.append('file', file);
      try {
        const response = await api.post('/upload/photo', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        urls.push(response.data.url);
      } catch { toast.error(`Failed to upload ${file.name}`); }
    }
    setFormData(prev => ({ ...prev, facility_images: [...prev.facility_images, ...urls] }));
    setUploadingImages(false);
  };

  const removeImage = (idx) => {
    setFormData(prev => ({ ...prev, facility_images: prev.facility_images.filter((_, i) => i !== idx) }));
  };

  const toggleSpecialty = (spec) => {
    setFormData(prev => {
      const updated = { ...prev.specialties_available };
      if (updated[spec]) { delete updated[spec]; }
      else { updated[spec] = DAYS_OF_WEEK; }
      return { ...prev, specialties_available: updated };
    });
  };

  const toggleSpecialtyDay = (spec, day) => {
    setFormData(prev => {
      const updated = { ...prev.specialties_available };
      const days = updated[spec] || [];
      updated[spec] = days.includes(day) ? days.filter(d => d !== day) : [...days, day];
      return { ...prev, specialties_available: updated };
    });
  };

  const toggleService = (svc) => {
    setFormData(prev => ({
      ...prev,
      services_available: prev.services_available.includes(svc)
        ? prev.services_available.filter(s => s !== svc)
        : [...prev.services_available, svc]
    }));
  };

  const toggleInsurance = (ins) => {
    setFormData(prev => ({
      ...prev,
      insurances_accepted: prev.insurances_accepted.includes(ins)
        ? prev.insurances_accepted.filter(i => i !== ins)
        : [...prev.insurances_accepted, ins]
    }));
  };

  const toggleModality = (mod) => {
    setFormData(prev => ({
      ...prev,
      imaging_modalities: prev.imaging_modalities.includes(mod)
        ? prev.imaging_modalities.filter(m => m !== mod)
        : [...prev.imaging_modalities, mod]
    }));
  };

  const getRelevantServices = () => {
    const selectedSpecs = Object.keys(formData.specialties_available);
    const type = formData.facility_type;
    const categories = ['General'];
    if (type === 'Hospital' || type === 'Multi-specialty Hospital') categories.push('Surgical', 'Maternal & Child', 'Diagnostic', 'Specialized');
    if (type === 'Dental Clinic' || selectedSpecs.some(s => s.includes('Dental'))) categories.push('Dental');
    if (type === 'Optical Clinic' || selectedSpecs.some(s => s.includes('Ophthalmology'))) categories.push('Optical');
    if (selectedSpecs.some(s => ['Oncology', 'Nephrology', 'Psychiatry', 'Neurology'].includes(s))) categories.push('Specialized');
    if (selectedSpecs.some(s => ['Obstetrics', 'Gynecology', 'Pediatrics'].includes(s))) categories.push('Maternal & Child');
    if (selectedSpecs.some(s => ['Radiology'].includes(s))) categories.push('Diagnostic');

    const uniqueCats = [...new Set(categories)];
    const allServices = [];
    uniqueCats.forEach(cat => {
      if (facilityServices[cat]) allServices.push(...facilityServices[cat]);
    });
    return [...new Set(allServices)];
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.terms_accepted) { toast.error('Please accept the Terms & Conditions'); return; }
    if (!formData.facility_name || !formData.facility_type || !formData.region) {
      toast.error('Please fill in all required fields'); return;
    }
    setLoading(true);
    try {
      const payload = { ...formData };
      if (payload.is_24_7) payload.opening_hours = { all: '24/7' };
      await api.post('/facilities', payload);
      toast.success('Facility registered successfully!');
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar user={user} />
      <div className="max-w-3xl mx-auto px-6 py-12">
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-8">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 bg-[#0F2A44] rounded-xl flex items-center justify-center">
              <Building2 className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="font-manrope font-bold text-2xl text-[#0F2A44]" data-testid="facility-reg-title">Register Your Facility</h1>
              <p className="text-sm text-[#4B5563]">List your healthcare facility on Afyagram</p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Facility Name */}
            <div>
              <label className="block font-manrope font-medium text-[#0F2A44] mb-2">Facility Name *</label>
              <Input value={formData.facility_name} onChange={(e) => setFormData({ ...formData, facility_name: e.target.value })} placeholder="e.g. Nairobi General Hospital" className="h-12" data-testid="facility-name-input" required />
            </div>

            {/* Facility Type */}
            <div>
              <label className="block font-manrope font-medium text-[#0F2A44] mb-2">Facility Type *</label>
              <select value={formData.facility_type} onChange={(e) => setFormData({ ...formData, facility_type: e.target.value })} className="w-full h-12 px-3 rounded-md border border-slate-200 text-sm" data-testid="facility-type-select" required>
                <option value="">Select facility type</option>
                {FACILITY_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>

            {/* Hospital Level */}
            {(formData.facility_type === 'Hospital' || formData.facility_type === 'Multi-specialty Hospital') && (
              <div>
                <label className="block font-manrope font-medium text-[#0F2A44] mb-2">Hospital Level</label>
                <select value={formData.hospital_level} onChange={(e) => setFormData({ ...formData, hospital_level: e.target.value })} className="w-full h-12 px-3 rounded-md border border-slate-200 text-sm" data-testid="hospital-level-select">
                  <option value="">Select level</option>
                  {HOSPITAL_LEVELS.map(l => <option key={l} value={l}>{l}</option>)}
                </select>
              </div>
            )}

            {/* Location & Region */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block font-manrope font-medium text-[#0F2A44] mb-2">Location / Address *</label>
                <Input value={formData.location} onChange={(e) => setFormData({ ...formData, location: e.target.value })} placeholder="Full address" className="h-12" data-testid="facility-location-input" required />
              </div>
              <div>
                <label className="block font-manrope font-medium text-[#0F2A44] mb-2">County *</label>
                <AutocompleteSelect options={counties} value={formData.region} onChange={(val) => setFormData({ ...formData, region: val })} placeholder="Select county" testId="facility-county-select" />
              </div>
            </div>

            {/* Pin Location */}
            <PinLocationPicker value={formData.pin_location} onChange={(loc) => setFormData({ ...formData, pin_location: loc })} />

            {/* Contact Person */}
            <div className="space-y-4">
              <h2 className="font-manrope font-semibold text-xl text-[#0F2A44]">Contact Person</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#0F2A44] mb-1">Full Name *</label>
                  <Input value={formData.contact_person_name} onChange={(e) => setFormData({ ...formData, contact_person_name: e.target.value })} placeholder="Contact person name" className="h-12" data-testid="contact-name-input" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#0F2A44] mb-1">Designation *</label>
                  <Input value={formData.contact_person_designation} onChange={(e) => setFormData({ ...formData, contact_person_designation: e.target.value })} placeholder="e.g. Medical Director" className="h-12" data-testid="contact-designation-input" required />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#0F2A44] mb-1">Email *</label>
                  <Input type="email" value={formData.contact_email} onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })} placeholder="contact@facility.com" className="h-12" data-testid="contact-email-input" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#0F2A44] mb-1">Phone *</label>
                  <Input value={formData.contact_phone} onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })} placeholder="+254 7XX XXX XXX" className="h-12" data-testid="contact-phone-input" required />
                </div>
              </div>
            </div>

            {/* Bio */}
            <div>
              <label className="block font-manrope font-medium text-[#0F2A44] mb-2">Brief Description</label>
              <Textarea value={formData.bio} onChange={(e) => setFormData({ ...formData, bio: e.target.value })} placeholder="Brief description of your facility, services, and mission..." rows={4} data-testid="facility-bio-input" />
            </div>

            {/* KMPDC Registration */}
            <div>
              <label className="block font-manrope font-medium text-[#0F2A44] mb-2">KMPDC Registration Number</label>
              <div className="flex gap-2">
                <Input value={formData.kmpdc_registration} onChange={(e) => { setFormData({ ...formData, kmpdc_registration: e.target.value }); setKmpdcVerified(null); }} placeholder="KMPDC Registration No." className="h-12 flex-1" data-testid="facility-kmpdc-input" />
                <Button type="button" onClick={handleVerifyKMPDC} disabled={verifying || !formData.kmpdc_registration} variant="outline" className="h-12" data-testid="facility-kmpdc-verify-btn">
                  {verifying ? <Loader className="h-4 w-4 animate-spin" /> : <Shield className="h-4 w-4 mr-1" />}
                  Verify
                </Button>
              </div>
              {kmpdcVerified === true && <p className="text-sm text-green-600 mt-1">Verified with KMPDC</p>}
              {kmpdcVerified === false && <p className="text-sm text-red-500 mt-1">Could not verify</p>}
            </div>

            {/* Working Hours */}
            <div className="space-y-3">
              <h2 className="font-manrope font-semibold text-xl text-[#0F2A44]">Working Hours</h2>
              <label className="flex items-center gap-3 cursor-pointer p-3 bg-slate-50 rounded-lg border border-slate-100">
                <input type="checkbox" checked={formData.is_24_7} onChange={(e) => setFormData({ ...formData, is_24_7: e.target.checked })} className="h-4 w-4 rounded text-[#2DD4BF]" data-testid="is-24-7-toggle" />
                <span className="font-medium text-[#0F2A44]">Open 24/7</span>
              </label>
              {!formData.is_24_7 && (
                <OpeningHoursEditor value={formData.opening_hours} onChange={(hours) => setFormData({ ...formData, opening_hours: hours })} />
              )}
            </div>

            {/* Specialties Available */}
            <div className="space-y-4">
              <h2 className="font-manrope font-semibold text-xl text-[#0F2A44]">Specialties Available</h2>
              <div className="max-h-80 overflow-y-auto p-4 border border-slate-200 rounded-lg space-y-3">
                {specialties.map(spec => (
                  <div key={spec} className="border-b border-slate-100 pb-2 last:border-0">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={!!formData.specialties_available[spec]} onChange={() => toggleSpecialty(spec)} className="h-4 w-4 rounded text-[#2DD4BF]" />
                      <span className="font-public text-sm font-medium text-[#0F2A44]">{spec}</span>
                    </label>
                    {formData.specialties_available[spec] && (
                      <div className="ml-6 mt-2 flex flex-wrap gap-2">
                        {DAYS_OF_WEEK.map(day => (
                          <label key={day} className="flex items-center gap-1 text-xs cursor-pointer">
                            <input type="checkbox" checked={(formData.specialties_available[spec] || []).includes(day)} onChange={() => toggleSpecialtyDay(spec, day)} className="h-3 w-3 rounded" />
                            <span className="text-[#4B5563]">{day.slice(0, 3)}</span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Imaging Modalities */}
            <div className="space-y-4">
              <h2 className="font-manrope font-semibold text-xl text-[#0F2A44]">Imaging Modalities</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 p-4 border border-slate-200 rounded-lg">
                {IMAGING_MODALITIES.map(mod => (
                  <label key={mod} className="flex items-center gap-2 cursor-pointer hover:bg-slate-50 p-2 rounded">
                    <input type="checkbox" checked={formData.imaging_modalities.includes(mod)} onChange={() => toggleModality(mod)} className="h-4 w-4 rounded text-[#2DD4BF]" />
                    <span className="font-public text-sm text-[#262626]">{mod}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Services Available */}
            {formData.facility_type && (
              <div className="space-y-4">
                <h2 className="font-manrope font-semibold text-xl text-[#0F2A44]">Services Available</h2>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 max-h-60 overflow-y-auto p-4 border border-slate-200 rounded-lg">
                  {getRelevantServices().map(svc => (
                    <label key={svc} className="flex items-center gap-2 cursor-pointer hover:bg-slate-50 p-2 rounded">
                      <input type="checkbox" checked={formData.services_available.includes(svc)} onChange={() => toggleService(svc)} className="h-4 w-4 rounded text-[#2DD4BF]" />
                      <span className="font-public text-sm text-[#262626]">{svc}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Insurances Accepted */}
            <div className="space-y-4">
              <h2 className="font-manrope font-semibold text-xl text-[#0F2A44]">Insurances Accepted</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 max-h-60 overflow-y-auto p-4 border border-slate-200 rounded-lg">
                {insurances.map(ins => (
                  <label key={ins} className="flex items-center gap-2 cursor-pointer hover:bg-slate-50 p-2 rounded">
                    <input type="checkbox" checked={formData.insurances_accepted.includes(ins)} onChange={() => toggleInsurance(ins)} className="h-4 w-4 rounded text-[#2DD4BF]" />
                    <span className="font-public text-sm text-[#262626]">{ins}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Facility Images */}
            <div className="space-y-4">
              <h2 className="font-manrope font-semibold text-xl text-[#0F2A44]">Facility Images (Optional)</h2>
              <div className="flex flex-wrap gap-3">
                {formData.facility_images.map((url, idx) => (
                  <div key={idx} className="relative w-24 h-24 rounded-lg overflow-hidden border border-slate-200">
                    <img src={url} alt={`Facility ${idx + 1}`} className="w-full h-full object-cover" />
                    <button type="button" onClick={() => removeImage(idx)} className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center">
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
                {formData.facility_images.length < 5 && (
                  <button type="button" onClick={() => fileInputRef.current?.click()} disabled={uploadingImages} className="w-24 h-24 rounded-lg border-2 border-dashed border-slate-300 hover:border-[#2DD4BF] flex flex-col items-center justify-center text-slate-400 hover:text-[#2DD4BF] transition-colors" data-testid="facility-image-upload-btn">
                    {uploadingImages ? <Loader className="h-5 w-5 animate-spin" /> : <><Upload className="h-5 w-5 mb-1" /><span className="text-xs">Upload</span></>}
                  </button>
                )}
              </div>
              <input ref={fileInputRef} type="file" accept="image/*" multiple className="hidden" onChange={handleImageUpload} />
              <p className="text-xs text-[#4B5563]">Upload up to 5 images of your facility</p>
            </div>

            {/* Signature Services */}
            <SignatureServices value={formData.signature_services} onChange={(svcs) => setFormData({ ...formData, signature_services: svcs })} />

            {/* Terms & Conditions */}
            <TermsConsent accepted={formData.terms_accepted} onChange={(val) => setFormData({ ...formData, terms_accepted: val })} />

            {/* Submit */}
            <div className="flex gap-4 pt-6">
              <Button type="button" onClick={() => navigate('/')} variant="outline" className="flex-1">Cancel</Button>
              <Button type="submit" disabled={loading || !formData.terms_accepted} data-testid="facility-register-submit" className="flex-1 bg-[#2DD4BF] hover:bg-[#2DD4BF]/90 text-[#0F2A44] font-manrope font-semibold disabled:opacity-50">
                {loading ? 'Registering...' : 'Register Facility'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
